import { useState } from "react";
import { Plus } from "lucide-react";
import { useLocation } from "wouter";
import AppShell from "@/components/foundations/core/AppShell/AppShell";
import CreateTaskSheet from "@/components/tasks/sheets/CreateTaskSheet/CreateTaskSheet";
import TaskCard from "@/components/tasks/lists/TaskCard/TaskCard";
import TaskSwipeActions from "@/components/tasks/lists/TaskSwipeActions/TaskSwipeActions";
import TaskStatusDivider, {
  shouldShowStatusDivider,
  statusCount,
  sortTasksByStatus,
  useCollapsedStatuses,
} from "@/components/tasks/lists/TaskStatusDivider/TaskStatusDivider";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { todayKey } from "@/lib/time";
import { deriveTaskLifecycle } from "@shared/taskLifecycle";
import BulkTaskActionBar from "@/components/navigation/BulkTaskActionBar/BulkTaskActionBar";

export default function Inbox() {
  const [, setLocation] = useLocation();
  const utils = trpc.useUtils();
  const inboxQuery = trpc.tasks.listInbox.useQuery({ today: todayKey() });
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const statusGroups = useCollapsedStatuses();

  const markDone = trpc.tasks.markDone.useMutation({
    onSuccess: () => utils.tasks.listInbox.invalidate(),
  });

  const toggleSkipped = trpc.tasks.toggleSkipped.useMutation({
    onSuccess: () => utils.tasks.listInbox.invalidate(),
  });

  function toggleSelected(id: number) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const tasks = sortTasksByStatus(inboxQuery.data ?? []);

  return (
    <AppShell
      backTo="/"
      title="Inbox"
      rightSlot={
        tasks.length > 0 && (
          <button
            onClick={() => {
              setSelectMode(s => !s);
              setSelected(new Set());
            }}
            className="text-sm uppercase tracking-wider px-3 h-9 rounded-full text-foreground hover:bg-accent transition-colors"
          >
            {selectMode ? "Done" : "Select"}
          </button>
        )
      }
    >
      <div className="container max-w-3xl py-6 space-y-6">
        <header className="flex items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Quick add
            </p>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight">
              Inbox
            </h1>
          </div>
          <div className="font-display text-3xl tabular-nums">
            {tasks.length}
          </div>
        </header>

        {inboxQuery.isLoading ? (
          <div className="space-y-2">
            {[0, 1, 2].map(i => (
              <div key={i} className="h-20 rounded-xl bg-muted/40 animate-pulse" />
            ))}
          </div>
        ) : tasks.length === 0 ? (
          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            className="w-full border border-dashed border-border rounded-2xl py-20 px-6 text-center space-y-3 hover:border-foreground/40 hover:bg-accent/30 transition-all"
          >
            <div className="font-display text-xl">Nothing in the inbox</div>
            <div className="text-sm text-foreground/70">
              Quick add something and sort it out later.
            </div>
          </button>
        ) : (
          <div className="space-y-2">
            {tasks.map((t, index) => {
              const isSelected = selected.has(t.id);
              const lifecycle = deriveTaskLifecycle(t);
              return (
                <div key={t.id} className="space-y-2">
                  {shouldShowStatusDivider(tasks, index) && (
                    <TaskStatusDivider
                      status={lifecycle}
                      count={statusCount(tasks, lifecycle)}
                      collapsed={statusGroups.collapsed.has(lifecycle)}
                      onToggle={() => statusGroups.toggle(lifecycle)}
                    />
                  )}
                  {!statusGroups.collapsed.has(lifecycle) && (
                    <TaskSwipeActions
                      task={t}
                      disabled={selectMode}
                      onSwipeRight={() =>
                        markDone.mutate({ id: t.id, clientTimestamp: new Date().toISOString() })
                      }
                      onSwipeLeft={() =>
                        toggleSkipped.mutate({ id: t.id, clientTimestamp: new Date().toISOString() })
                      }
                      onLongPress={() => {
                        setSelectMode(true);
                        toggleSelected(t.id);
                      }}
                    >
                      <TaskCard
                        task={t}
                        selected={isSelected}
                        onClick={() => {
                          if (selectMode) toggleSelected(t.id);
                          else setLocation(`/task/${t.id}`);
                        }}
                      />
                    </TaskSwipeActions>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {tasks.length > 0 && !selectMode && (
          <div className="fixed bottom-6 right-6 z-30 flex flex-col gap-2 items-end">
            <Button
              onClick={() => setSheetOpen(true)}
              size="lg"
              className="h-14 w-14 rounded-full shadow-lg gap-0 p-0 bg-card text-foreground border border-border hover:bg-accent"
              aria-label="Add"
            >
              <Plus className="h-5 w-5" />
            </Button>
          </div>
        )}

        {selectMode && selected.size > 0 && (
          <BulkTaskActionBar
            selectedIds={Array.from(selected)}
            onComplete={() => {
              setSelectMode(false);
              setSelected(new Set());
            }}
          />
        )}
      </div>
      <CreateTaskSheet open={sheetOpen} onOpenChange={setSheetOpen} />
    </AppShell>
  );
}
